import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { parameterizableSecretBaseToModel, parameterizablePasswordOrAKVtoSecretBase } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { ValueAdapter } from '../value.adapter';
import {
    ITridentResource,
    ParameterConfig,
    snowflakeAdditionalFieldsAllowedKeys,
    snowflakeAdditionalPropertiesAdapts,
    SnowflakeAdditionalPropertiesKeyType,
    snowflakeV2AuthTypes,
    SnowflakeV2LinkedService
} from 'shell-models-lib';
import { toParameterClass, toParameterObject } from './parameter-config.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SnowflakeV2LinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            // ADF only
            version: !__IS_FABRIC_DPE__ ? '*' : undefined,
            typeProperties: {
                accountIdentifier: '*', // accountName
                user: '*',
                database: '*',
                warehouse: '*',
                role: '*',
                authenticationType: '*',
                password: 'secret',
                privateKey: 'secret',
                privateKeyPassphrase: 'secret',
                tenantId: '*',
                clientId: '*',
                clientSecret: 'secret',
                scope: '*',
                host: '*',
                encryptedCredential: '*',
                ...snowflakeAdditionalPropertiesAdapts
            },
            parameters: '*'
        }
    }
})
export class SnowflakeV2LinkedServiceAdapter extends LinkedServiceAdapter<SnowflakeV2LinkedService> {
    protected supportFirstClassParameterExperience = true;
    protected modelConstructor(): new () => SnowflakeV2LinkedService {
        return SnowflakeV2LinkedService;
    }

    protected model(): IModel {
        return SnowflakeV2LinkedService.MODEL;
    }

    protected override toAzureTypeProperties = (tridentResource: ITridentResource) => {
        const { extensionDataSourcePath } = JSON.parse(tridentResource.connectionDetails);
        const [accountIdentifier, warehouse] = extensionDataSourcePath.split(';'); // Fabric warehouse cannot contain ';'
        return {
            accountIdentifier,
            warehouse
        };
    };

    protected async propertiesToResourceAsync(linkedService: SnowflakeV2LinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.SnowflakeV2LinkedService;
        properties.type = Metamodel.modelFor(SnowflakeV2LinkedService).name;

        let password: string | Model.SecretBase;
        let privateKey: string | Model.SecretBase;
        let privateKeyPassphrase: string | Model.SecretBase;
        let tenantId: string;
        let clientId: string;
        let clientSecret: string | Model.SecretBase;
        let scope: string;

        if (linkedService.authenticationType === snowflakeV2AuthTypes.Basic) {
            password = parameterizablePasswordOrAKVtoSecretBase(linkedService, SnowflakeV2LinkedService.PASSWORD, this.context);
        } else if (linkedService.authenticationType === snowflakeV2AuthTypes.KeyPair) {
            privateKey = parameterizablePasswordOrAKVtoSecretBase(linkedService, SnowflakeV2LinkedService.PRIVATE_KEY, this.context);
            if (linkedService.privateKeyPassphrase) {
                privateKeyPassphrase = parameterizablePasswordOrAKVtoSecretBase(linkedService, SnowflakeV2LinkedService.PRIVATE_KEY_PASSPHRASE, this.context);
            }
        } else if (linkedService.authenticationType === snowflakeV2AuthTypes.AADServicePrincipal) {
            tenantId = this.valueToResource(linkedService, SnowflakeV2LinkedService.TENANT_ID);
            clientId = this.valueToResource(linkedService, SnowflakeV2LinkedService.CLIENT_ID);
            clientSecret = parameterizablePasswordOrAKVtoSecretBase(linkedService, SnowflakeV2LinkedService.CLIENT_SECRET, this.context);
            scope = this.valueToResource(linkedService, SnowflakeV2LinkedService.SCOPE);
        }

        properties.typeProperties = {
            authenticationType: linkedService.authenticationType,
            accountIdentifier: this.valueToResource(linkedService, SnowflakeV2LinkedService.ACCOUNT_IDENTIFIER),
            user: this.valueToResource(linkedService, SnowflakeV2LinkedService.USER_NAME),
            database: this.valueToResource(linkedService, SnowflakeV2LinkedService.DATABASE),
            warehouse: this.valueToResource(linkedService, SnowflakeV2LinkedService.WAREHOUSE),
            role: this.valueToResource(linkedService, SnowflakeV2LinkedService.ROLE) || undefined,
            host: this.valueToResource(linkedService, SnowflakeV2LinkedService.HOST) || undefined,
            password,
            privateKey,
            privateKeyPassphrase,
            tenantId,
            clientId,
            clientSecret,
            scope
        };

        if (this._hasVersion) {
            properties.version = linkedService.version;
        }
        if (linkedService.parameterizableAdditionalFields?.length > 0) {
            properties.typeProperties = {
                ...properties.typeProperties,
                ...toParameterObject(linkedService.parameterizableAdditionalFields, false, this.valueToResource)
            };
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: SnowflakeV2LinkedService): Promise<SnowflakeV2LinkedService> {
        const properties = linkedServiceResource.properties as Model.SnowflakeV2LinkedService;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const typeProperties: Model.SnowflakeV2LinkedServiceTypeProperties = properties.typeProperties;

        ValueAdapter.toModel(linkedService, SnowflakeV2LinkedService.ACCOUNT_IDENTIFIER, typeProperties.accountIdentifier);
        ValueAdapter.toModel(linkedService, SnowflakeV2LinkedService.USER_NAME, typeProperties.user);
        ValueAdapter.toModel(linkedService, SnowflakeV2LinkedService.DATABASE, typeProperties.database);
        ValueAdapter.toModel(linkedService, SnowflakeV2LinkedService.WAREHOUSE, typeProperties.warehouse);
        ValueAdapter.toModel(linkedService, SnowflakeV2LinkedService.ROLE, typeProperties.role);
        ValueAdapter.toModel(linkedService, SnowflakeV2LinkedService.HOST, typeProperties.host);

        linkedService.authenticationType = typeProperties.authenticationType || snowflakeV2AuthTypes.Basic;

        if (linkedService.authenticationType === snowflakeV2AuthTypes.Basic) {
            parameterizableSecretBaseToModel(linkedService, SnowflakeV2LinkedService.PASSWORD, typeProperties.password, this.context);
        } else if (linkedService.authenticationType === snowflakeV2AuthTypes.KeyPair) {
            parameterizableSecretBaseToModel(linkedService, SnowflakeV2LinkedService.PRIVATE_KEY, typeProperties.privateKey, this.context);
            parameterizableSecretBaseToModel(linkedService, SnowflakeV2LinkedService.PRIVATE_KEY_PASSPHRASE, typeProperties.privateKeyPassphrase, this.context);
        } else if (linkedService.authenticationType === snowflakeV2AuthTypes.AADServicePrincipal) {
            ValueAdapter.toModel(linkedService, SnowflakeV2LinkedService.CLIENT_ID, typeProperties.clientId);
            ValueAdapter.toModel(linkedService, SnowflakeV2LinkedService.SCOPE, typeProperties.scope);
            parameterizableSecretBaseToModel(linkedService, SnowflakeV2LinkedService.CLIENT_SECRET, typeProperties.clientSecret, this.context);
        }

        if (this._hasVersion || properties.version) {
            linkedService.version = properties.version;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const additionalPropertiesObj: { [key: string]: any } = {};
        for (const propertyName in typeProperties) {
            if (snowflakeAdditionalFieldsAllowedKeys.includes(propertyName as SnowflakeAdditionalPropertiesKeyType)) {
                additionalPropertiesObj[propertyName] =
                    typeProperties[propertyName as keyof Model.SnowflakeV2LinkedServiceTypeProperties];
            }
        }
        linkedService.parameterizableAdditionalFields = toParameterClass(
            additionalPropertiesObj,
            linkedService
        ) as Array<ParameterConfig<SnowflakeAdditionalPropertiesKeyType>>;
        return linkedService;
    }

    private get _hasVersion(): boolean {
        // only ADF version is stored in LS, fabric is in script activity
        return !__IS_FABRIC_DPE__;
    }
}
